import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  TypographyProps,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import moment from 'moment';

import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import { adminGetUserById, getUsersState } from '../../../store/UserSlice';
import {
  getSubmissionState,
  adminGetSubmissionList,
  adminLoadMoreSubmissionList,
} from '../../../store/SubmissionSlice';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import StudentSubmissionListItem from './components/StudentSubmissionListItem';
import { checkIsUser, getShortName } from '../../../helper';
import StudentsJumbotron from './components/StudentsJumbotron';

interface IFilterData {
  limit: number;
  offset: number;
}

interface IState {
  filterData: IFilterData;
}

// STYLES
const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};
const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

// DATA
const defaultState: IState = {
  filterData: {
    limit: 30,
    offset: 0,
  },
};

const StudentSubmissions: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const usersState = useAppSelector(getUsersState);
  const submissionState = useAppSelector(getSubmissionState);
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(defaultState);

  useEffect(() => {
    if (params.userId) {
      dispatch(adminGetUserById(params.userId));
      dispatch(
        adminGetSubmissionList({
          ...state.filterData,
          userId: params.userId,
        })
      );
    }
  }, []);

  // METHODS
  const handleLoadMore = () => {
    if (params.userId) {
      const newOffset = submissionState.list.length;

      dispatch(
        adminLoadMoreSubmissionList({
          ...state.filterData,
          offset: newOffset,
          userId: params.userId,
        })
      );

      setState((prevState) => ({
        ...prevState,
        filterData: {
          ...prevState.filterData,
          offset: newOffset,
        },
      }));
    }
  };

  const isLoading =
    usersState.status === 'loading' || submissionState.status === 'loading';

  const _student = usersState.user;

  let studentName = '';
  let lastLogin = '-';

  if (_student) {
    studentName = getShortName(
      _student.firstname,
      _student.lastname,
      !checkIsUser(authState.user)
    );

    if (_student.lastLogin) {
      lastLogin = moment(_student.lastLogin).format('YYYY-MM-DD hh:mm A');
    }
  }

  if (studentName.trim() === '') {
    studentName = '-';
  }

  return (
    <CustomGrayContainer
      style={{
        padding: 0,
        borderRadius: 0,
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      <StudentsJumbotron onBack={() => navigate(-1)} />
      <Box style={{ padding: '0 30px 15px 30px' }}>
        <div style={{ marginBottom: '30px' }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ maxWidth: '1300px' }}
          >
            {_student && (
              <>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Student Name"
                    titleProps={titleProps}
                    description={studentName}
                    descriptionProps={descriptionProps}
                    avatarProps={{
                      src: _student.avatar,
                      alt: studentName,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <DetailViewCard
                    title="Last Accessed"
                    titleProps={titleProps}
                    description={lastLogin}
                    descriptionProps={descriptionProps}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </div>
        <div style={{ marginBottom: '30px' }}>
          {submissionState.list.map((submission, index) => (
            <StudentSubmissionListItem
              key={index}
              attempt={index + 1}
              submission={submission}
            />
          ))}
          {isLoading && (
            <div style={{ padding: '30px 0' }}>
              <BasicLoader />
            </div>
          )}
          {submissionState.count > submissionState.list.length && (
            <Stack
              direction="row"
              justifyContent="center"
              style={{ marginBottom: '30px' }}
            >
              <Button
                disabled={submissionState.status === 'loading'}
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            </Stack>
          )}
          {submissionState.list.length === 0 &&
            submissionState.status !== 'loading' && (
              <Typography variant="h6">No Quizzes are available</Typography>
            )}
        </div>
      </Box>
    </CustomGrayContainer>
  );
};

export default StudentSubmissions;
